/**
 * Base client for EIP-712 signed API operations
 * Shared by FiClient and EcoClient: HTTP client, wallet init,
 * nonce management, signature builder, and hex-to-base64 signature conversion
 */

import { HttpClient } from "../../transport/http/HttpClient";
import mormSigner from "../../signing/mormSigner";
import { NonceManager } from "../../utils/nonceManager";
import { InfoClient } from "../info/InfoClient";
import { BaseSignatureBuilder, NonceProvider } from "./BaseSignatureBuilder";
import { BaseSignedClientConfig } from "../../types/client";

export abstract class BaseSignedClient {
  protected httpClient: HttpClient;
  protected infoClient: InfoClient;
  protected nonceManager?: NonceManager;
  protected mnemonic?: string;
  protected chainID: number;
  protected walletAddress?: string;
  protected nonceCounter: number | null = null;
  protected signatureBuilder?: BaseSignatureBuilder;

  constructor(config: BaseSignedClientConfig) {
    this.httpClient = new HttpClient(config.baseURL, config.maxRetries ?? 3);
    this.infoClient = new InfoClient({
      baseURL: config.baseURL,
      sessionToken: config.sessionToken,
    });
    this.chainID = config.chainID ?? 10900;
    this.mnemonic = config.mnemonic;
    if (config.mnemonic) {
      this.nonceManager = new NonceManager(this.infoClient);
    }
  }

  /**
   * Get or create signature builder. Must be called after initialize() when walletAddress is set.
   */
  protected getSignatureBuilder(): BaseSignatureBuilder {
    if (!this.signatureBuilder) {
      if (!this.walletAddress) {
        throw new Error(
          this.getNotInitializedMessage(),
        );
      }
      if (!this.mnemonic) {
        throw new Error("Mnemonic is required for write operations");
      }
      this.signatureBuilder = new BaseSignatureBuilder({
        mnemonic: this.mnemonic,
        walletAddress: this.walletAddress,
        chainID: this.chainID,
      });
    }
    return this.signatureBuilder;
  }

  /**
   * Override in subclass to customize "not initialized" error message.
   */
  protected getNotInitializedMessage(): string {
    return "Client not initialized. Call initialize() first.";
  }

  /**
   * Get NonceManager. Throws if mnemonic was not provided (no NonceManager).
   */
  protected getNonceManager(): NonceManager {
    if (!this.nonceManager) {
      throw new Error("NonceManager not initialized. Mnemonic required.");
    }
    return this.nonceManager;
  }

  /**
   * Get nonce provider adapter for BaseSignatureBuilder.
   * Throws if mnemonic was not provided (no NonceManager).
   */
  protected getNonceProvider(): NonceProvider {
    return {
      getNextNonce: () => this.getNextNonce(),
    };
  }

  /**
   * Initialize wallet address from mnemonic. Required before signed operations.
   */
  async initialize(): Promise<void> {
    if (!this.mnemonic) {
      throw new Error("Mnemonic is required for write operations");
    }
    await mormSigner.initialize();
    const result = await mormSigner.generateAddress(this.mnemonic, 0);
    if (!result.success || !result.address) {
      throw new Error(`Failed to generate wallet address: ${result.error}`);
    }
    this.walletAddress = result.address;
    this.signatureBuilder = new BaseSignatureBuilder({
      mnemonic: this.mnemonic,
      walletAddress: this.walletAddress,
      chainID: this.chainID,
    });
  }

  /**
   * Get current wallet address. Throws if not initialized.
   */
  getWalletAddress(): string {
    if (!this.walletAddress) {
      throw new Error(this.getNotInitializedMessage());
    }
    return this.walletAddress;
  }

  /**
   * Refresh nonce from server (e.g. on nonce errors). Throws if no NonceManager.
   */
  async refreshNonceFromServer(): Promise<void> {
    const nm = this.getNonceManager();
    const walletAddress = this.getWalletAddress();
    const nonceResult = await nm.getExpectedNextNonce(
      walletAddress,
      false,
    );
    this.nonceCounter = nonceResult.next_nonce;
    console.log("[BaseSignedClient] Refreshed nonce from server", {
      walletAddress,
      newNonce: this.nonceCounter,
    });
  }

  /**
   * Get next nonce (lazy init from server, then increment locally). Throws if no NonceManager.
   */
  protected async getNextNonce(): Promise<string> {
    this.getNonceManager(); // ensure present
    if (this.nonceCounter === null) {
      await this.refreshNonceFromServer();
    }
    this.nonceCounter!++;
    return this.nonceCounter!.toString();
  }

  /**
   * Convert ECDSA signature from hex to base64 for TxRequest.signatures (ingress expects base64).
   */
  protected hexSignatureToBase64(signature: string): string {
    const raw = signature.replace(/^0x/i, "");
    if (/^[0-9a-fA-F]+$/.test(raw) && (raw.length === 128 || raw.length === 130)) {
      const len = raw.length / 2;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = parseInt(raw.slice(i * 2, i * 2 + 2), 16);
      }
      return btoa(String.fromCharCode(...bytes));
    }
    return signature;
  }
}
